import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Clapperboard, Wand2, Upload, Image as ImageIcon, Film, CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import api, { getErr } from "../../lib/api";
import { NeonButton, NeonCard, Spinner, Pill } from "../../components/Neon";

const Label = ({ children }) => <label className="text-xs uppercase tracking-widest text-[var(--text-muted)]">{children}</label>;
const mediaUrl = (p) => p?.animated_url || p?.video_url || null;

export default function AdminHeroStudio() {
  const [loading, setLoading] = useState(true);
  const [providerReady, setProviderReady] = useState(false);
  const [comps, setComps] = useState([]);
  const [projects, setProjects] = useState([]);

  const [compId, setCompId] = useState("");
  const [prompt, setPrompt] = useState("make the background sparkle and add a soft camera flash");
  const [imageB64, setImageB64] = useState(null);
  const [imageName, setImageName] = useState("");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null); // current job {id,status,message,...}
  const [settingHero, setSettingHero] = useState("");
  const pollRef = useRef(null);

  const selComp = comps.find((c) => c.id === compId) || null;

  const loadProjects = () => api.get("/admin/studio/projects").then(({ data }) => setProjects((Array.isArray(data) ? data : []).filter((p) => (p.type || p.kind) === "animation"))).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get("/admin/studio/options").then((r) => r.data).catch(() => ({})),
      api.get("/competitions").then((r) => r.data).catch(() => []),
    ]).then(([opts, c]) => {
      setProviderReady(!!opts.video_provider_ready);
      setComps(Array.isArray(c) ? c : []);
    }).finally(() => setLoading(false));
    loadProjects();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Please pick an image under 8MB."); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const res = String(reader.result || "");
      setImageB64(res.includes(",") ? res.split(",")[1] : res);
      setImageName(file.name);
    };
    reader.readAsDataURL(file);
  };

  // Poll a render job every 8s (matches the app) until it finishes.
  const startPolling = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/studio/job/${id}`);
        setJob(data);
        const st = String(data.status || "").toLowerCase();
        if (["completed", "error", "failed"].includes(st)) {
          clearInterval(pollRef.current); pollRef.current = null;
          if (st === "completed") { toast.success("Hero video ready — attached to the competition."); loadProjects(); }
          else toast.error(data.message || "Render failed.");
        }
      } catch { /* keep polling */ }
    }, 8000);
  };

  const animate = async () => {
    if (!prompt.trim()) { toast.error("Describe the animation (e.g. 'add sparkle and camera flash')."); return; }
    const body = { prompt: prompt.trim(), output_format: "mp4" };
    if (compId) body.competition_id = compId; // auto-attaches hero_video on completion
    if (imageB64) body.image_base64 = imageB64;
    else if (selComp?.hero_image) body.image_url = selComp.hero_image;
    else { toast.error("Upload a prize photo or pick a competition that has a hero image."); return; }

    setBusy(true); setJob(null);
    try {
      const { data } = await api.post("/admin/studio/animate", body);
      setJob(data);
      const st = String(data.status || "").toLowerCase();
      if (st === "mocked") toast.message("Animation queued in MOCK mode — add a fal.ai key to render.");
      else if (st === "completed") { toast.success("Hero video ready — attached."); loadProjects(); }
      else { toast.success("Animation started — rendering…"); startPolling(data.id); }
      loadProjects();
    } catch (e) { toast.error(getErr(e)); } finally { setBusy(false); }
  };

  const setAsHero = async (projectId) => {
    if (!compId) { toast.error("Pick a competition to attach this hero to."); return; }
    setSettingHero(projectId);
    try {
      await api.post(`/admin/studio/projects/${projectId}/set-hero`, { competition_id: compId });
      toast.success("Set as the competition's hero video.");
    } catch (e) { toast.error(getErr(e)); } finally { setSettingHero(""); }
  };

  if (loading) return <Spinner />;
  const jobSt = String(job?.status || "").toLowerCase();
  const jobMedia = mediaUrl(job);

  return (
    <div data-testid="admin-hero-studio" className="max-w-3xl space-y-5">
      <div className="flex items-center gap-2">
        <Clapperboard className="h-6 w-6 text-[var(--neon-cyan)]" />
        <h2 className="font-display uppercase text-2xl text-white">Animated Heroes</h2>
      </div>
      <p className="text-sm text-[var(--text-muted)]">Turn a prize photo into a looping hero video that autoplays on the competition cards and detail page. Pick a competition to auto-attach the result to its <span className="font-mono text-white">hero_video</span>.</p>

      {!providerReady && (
        <div className="flex items-start gap-3 rounded-xl border border-[rgba(255,210,74,.4)] bg-[rgba(255,210,74,.06)] p-4" data-testid="studio-mock-note">
          <AlertTriangle className="h-5 w-5 text-[var(--neon-gold)] shrink-0" />
          <p className="text-sm text-[var(--text-muted)]">The render engine is in <span className="text-white">MOCK mode</span>. Add a <span className="text-white">fal.ai</span> key in Settings → API Key Vault to produce real videos. The full flow works now and will render live the moment the key is added.</p>
        </div>
      )}

      <NeonCard variant="cyan" innerClassName="p-6 space-y-4">
        <div>
          <Label>Competition (auto-attach hero to)</Label>
          <select value={compId} onChange={(e) => setCompId(e.target.value)} data-testid="studio-comp-select" className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm">
            <option value="">— Select a competition —</option>
            {comps.map((c) => <option key={c.id} value={c.id}>{c.title}{c.hero_video ? "  🎬 has video" : ""}</option>)}
          </select>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Source image</Label>
            <label className="mt-1 flex items-center gap-2 rounded-lg border border-dashed border-white/20 px-3 py-2.5 text-sm text-[var(--text-muted)] cursor-pointer hover:border-[var(--neon-cyan)]">
              <Upload className="h-4 w-4" /> {imageName || "Upload a prize photo"}
              <input type="file" accept="image/*" onChange={onFile} data-testid="studio-file" className="hidden" />
            </label>
            {!imageB64 && selComp?.hero_image && <p className="mt-1 text-[11px] text-[var(--text-muted)] flex items-center gap-1"><ImageIcon className="h-3 w-3" /> Will use the competition's current hero image.</p>}
          </div>
          <div className="flex items-end">
            {(imageB64 || selComp?.hero_image) && (
              <img src={imageB64 ? `data:image/*;base64,${imageB64}` : selComp.hero_image} alt="source" className="h-20 w-full max-w-[220px] rounded-lg object-cover border border-white/10" />
            )}
          </div>
        </div>

        <div>
          <Label>Animation prompt</Label>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={2} data-testid="studio-prompt"
            placeholder="e.g. make the car headlights flash, add gold confetti and slow zoom"
            className="mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[var(--neon-cyan)]" />
        </div>

        <NeonButton variant="pink" onClick={animate} loading={busy} data-testid="studio-animate"><Wand2 className="h-4 w-4" /> Animate into hero video</NeonButton>

        {job && (
          <div className="rounded-xl border border-white/10 p-4" data-testid="studio-job">
            <div className="flex items-center gap-2">
              {["processing", "queued", "mocked"].includes(jobSt) ? <Loader2 className="h-4 w-4 animate-spin text-[var(--neon-cyan)]" /> : jobSt === "completed" ? <CheckCircle2 className="h-4 w-4 text-[var(--neon-green)]" /> : <AlertTriangle className="h-4 w-4 text-[var(--neon-gold)]" />}
              <Pill color={jobSt === "completed" ? "green" : jobSt === "error" || jobSt === "failed" ? "red" : "gold"}>{(job.status || "").toUpperCase()}</Pill>
              {["processing", "queued"].includes(jobSt) && <span className="text-xs text-[var(--text-muted)]">rendering — auto-checking every 8s…</span>}
            </div>
            {job.message && <p className="mt-2 text-xs text-[var(--text-muted)]">{job.message}</p>}
            {jobMedia && (
              <video src={jobMedia} controls autoPlay muted loop playsInline className="mt-3 w-full aspect-video rounded-lg border border-white/10" data-testid="studio-job-video" />
            )}
          </div>
        )}
      </NeonCard>

      {/* Media library */}
      <NeonCard variant="soft" innerClassName="p-5">
        <div className="flex items-center gap-2 text-white"><Film className="h-4 w-4 text-[var(--neon-cyan)]" /><span className="font-display uppercase tracking-widest text-sm">Media Library ({projects.length})</span></div>
        {projects.length === 0 ? (
          <p className="mt-3 text-sm text-[var(--text-muted)]">No animations yet — create one above.</p>
        ) : (
          <ul className="mt-3 space-y-2" data-testid="studio-projects">
            {projects.slice(0, 12).map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/10 px-3 py-2">
                <div className="min-w-0 text-sm">
                  <span className="text-white line-clamp-1">{p.prompt || "Animation"}</span>
                  <Pill color={String(p.status).toLowerCase() === "completed" ? "green" : "gold"}>{String(p.status || "").toUpperCase()}</Pill>
                </div>
                <NeonButton variant="ghost" onClick={() => setAsHero(p.id)} loading={settingHero === p.id} disabled={!mediaUrl(p)} data-testid={`studio-sethero-${p.id}`}>Set as hero</NeonButton>
              </li>
            ))}
          </ul>
        )}
      </NeonCard>
    </div>
  );
}
